import { Vue, Options } from 'vue-class-component';
import {
  BuildingCreateDTO,
  EntrancePlantDetailDTO,
  EntrancePlantStructureCreateDTO,
} from '@/../client-generator/generated-clients/plants';
import { AddressingType } from '@/../client-generator/generated-clients/plants';
import CardContainer from '@/views/components/CardContainer/CardContainer.vue';
import { toLabel } from '@/core/utils/EntrancePlant/denomination-helper';
import { noAppartmentOptions } from '@/core/utils/EntrancePlant/entrance-plant.utils';
import plantsClient from '@/core/services/plants';

@Options({
  name: 'DialogEntrancePlantCreateStep3',
  components: {
    CardContainer,
  },
  props: {
    plant: {
      required: true,
      type: Object as () => EntrancePlantDetailDTO,
    },
    plantId: {
      type: Number,
      required: false,
    },   
    isEdit: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    plant: function (newValue) {
      if (newValue) this.initializeData();
    },
  },
  emits: ['is-loading', 'onBack', 'onNext', 'plant-edited'],
  computed: {},
})
export default class DialogEntrancePlantCreateStep3 extends Vue {
  plant!: EntrancePlantDetailDTO;
  plantId!: number;
  buildings: BuildingCreateDTO[] = [];
  cantAddBuildingString = '';

  get titleCard() {
    return `${this.buildings.length} ${this.$t(
      this.buildings.length == 1
        ? 'AppResourceEP.Building'
        : 'AppResourceEP.Buildings'
    ).toString()}`;
  }

  noAppartmentOptions(buldingIndex: number) {
    const otherBuildings = this.buildings.filter(
      (building, index) => index !== buldingIndex
    );
    const totalApartments = otherBuildings.reduce(
      (acc, building) => acc + building.apartmentsCount!,
      0
    );
    return noAppartmentOptions(3999-totalApartments);
  } 

  addBuilding() {
    if(this.noAppartmentOptions(this.buildings.length).length === 0) {
      this.cantAddBuilding();
      return;
    }
    this.buildings.push(
      new BuildingCreateDTO({
        name: this.getNewBuildingName(this.plant),
        apartmentsCount: 1,
      })
    );
  }

  cantAddBuilding() {
    this.cantAddBuildingString = this.$t('entrancePlant.apartmentLimitReached', { maxApartments: 3999 });
    setTimeout(() => {
      this.cantAddBuildingString = '';
    }, 4000);
  }

  mounted(): void {
    this.initializeData();
  }

  initializeData() {
    this.fillBuildings();
    if (this.buildings.length === 0) {
      this.addBuilding();
    }
  }

  fillBuildings() {
    this.plant.buildings?.forEach((building) => {
      this.buildings.push(
        new BuildingCreateDTO({
          apartmentsCount: building.apartments?.length,
          buildingId: building.buildingId,
          name: building.name,
        })
      );
    });
  }

  handleBuildingRemove(index: number) {
    this.buildings.splice(index, 1);
  }

  private getNewBuildingName(plant: EntrancePlantDetailDTO): string {
    let newBuildingName: string;
    let buildingIndex: number;
    let counter = 0;
    do {
      counter++;
      buildingIndex = this.buildings.length + counter;
      newBuildingName = `${this.$t('AppResourceEP.Building')} ${toLabel(
        counter,
        plant.denominationBuildings
      )}`;
    } while (this.buildings.some((x) => x.name == newBuildingName));
    return newBuildingName;
  }

  getDataToSave() {
    return new EntrancePlantStructureCreateDTO({
      wizardStep: 3,
      buildingsCreate: this.buildings,
    });
  }

  handleBackButton() {
    this.$emit('onBack', 'DialogEntrancePlantCreateStep2');
  }

  async handleNextButton() {
    await this.updatePlantStructure(this.getDataToSave());

    const nextPage =
      this.plant.addressingType === AddressingType.BuildingRiserFloorApartment
        ? 'DialogEntrancePlantCreateStep4'
        : 'DialogEntrancePlantCreateStep6';

    this.$emit('onNext', nextPage);
  }

  async updatePlantStructure(structure: EntrancePlantStructureCreateDTO) {
    if (!this.plantId) return;
    this.$emit('is-loading', true);

    try {
      await plantsClient.structure(this.plant.desktopId!, structure);
    } catch (error) {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 6000,
      });
    } finally {
      this.$emit('is-loading', false);
    }
  }
}
